import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { runGeneration } from "@/lib/generate";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGE_GROUPS, LANGUAGES, label } from "@/lib/activityData";

export default function BookCompanion() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", author: "", age_group: "", description: "", language: "bilingual" });
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.title || !form.age_group) { toast.error(lang === "es" ? "Ingresa el título y grupo de edad." : "Enter a book title and age group."); return; }
    setLoading(true); setElapsed(0);
    try {
      const j = await runGeneration("book", form, { onTick: setElapsed });
      toast.success(t("activity.saved"));
      navigate(`/activities/${j.result_id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <GeneratingOverlay open={loading} elapsed={elapsed} />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("nav.dashboard")}
        </button>
        <div className="space-y-2">
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Compañero de Libro" : "Book Companion"}</h1>
          <p className="text-muted-foreground text-lg">{lang === "es" ? "Preguntas y actividades bilingües para cualquier libro infantil." : "Bilingual questions and activities for any children's book."}</p>
        </div>
        <div className="space-y-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{lang === "es" ? "Título del libro" : "Book title"} <span className="text-secondary">*</span></Label>
              <Input value={form.title} onChange={set("title")} className="h-12 rounded-xl" data-testid="book-title" />
            </div>
            <div className="space-y-2">
              <Label>{lang === "es" ? "Autor" : "Author"}</Label>
              <Input value={form.author} onChange={set("author")} className="h-12 rounded-xl" data-testid="book-author" />
            </div>
            <div className="space-y-2">
              <Label>{t("generator.ageGroup")} <span className="text-secondary">*</span></Label>
              <Select value={form.age_group} onValueChange={set("age_group")}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="book-age"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("generator.language")}</Label>
              <Select value={form.language} onValueChange={set("language")}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="book-lang"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{label(l, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{lang === "es" ? "Descripción breve (opcional)" : "Short description (optional)"}</Label>
            <Textarea rows={3} value={form.description} onChange={set("description")} className="rounded-xl resize-none" placeholder={lang === "es" ? "De qué trata el libro..." : "What the book is about..."} data-testid="book-desc" />
          </div>
          <p className="text-xs text-muted-foreground">{lang === "es" ? "No se reproduce texto con derechos de autor." : "Copyrighted book text is never reproduced."}</p>
          <Button onClick={submit} disabled={loading} className="w-full py-3 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="book-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("generator.generating")}</> : <><BookOpen className="h-5 w-5" /> {t("common.generate")}</>}
          </Button>
        </div>
      </main>
    </div>
  );
}
